import { type FC, type ReactNode, Suspense, useCallback, useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';
import { useHomePageContext } from '../HomePageContext.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { getBuyerId } from 'dibs-cookie-jar';
import { TRADE } from 'dibs-buyer-layout/exports/userTypeEnums.ts';

//components
import {
    HpSharedCarouselTabs,
    type HpCarouselTab,
} from '../HpSharedCarouselTabs/HpSharedCarouselTabs.tsx';
import { ItemTilesCarouselShimmer } from 'dibs-product-tile-carousel/exports/ItemTilesCarouselShimmer.ts';
import { newArrivalsVariantTitle } from '../hpMessages.tsx';
import { HpSharedCarouselTabsShimmer } from '../HpSharedCarouselTabs/HpSharedCarouselTabsShimmer.tsx';

// styles
import dibsCss from 'dibs-css';

import { type HpSharedNewArrivalsTabContent_viewer$key } from './__generated__/HpSharedNewArrivalsTabContent_viewer.graphql.ts';
import { type HpSharedNewArrivalsTabContent_user$key } from './__generated__/HpSharedNewArrivalsTabContent_user.graphql.ts';
import { type HpSharedNewArrivalsCarouselTabs_personalizationCollections$key } from './__generated__/HpSharedNewArrivalsCarouselTabs_personalizationCollections.graphql.ts';

// lazy components
const HpSharedNewArrivalsTabContent = dibsLazy(
    'HpSharedNewArrivalsTabContent',
    () =>
        import(
            /* webpackChunkName: "HpSharedNewArrivalsTabContent" */ './HpSharedNewArrivalsTabContent.tsx'
        )
);

const personalizationCollectionsFragment = graphql`
    fragment HpSharedNewArrivalsCarouselTabs_personalizationCollections on PersonalizationCollectionType
    @relay(plural: true) {
        categoryTitle
        linkData {
            path
        }
        categoryItems {
            serviceId
        }
    }
`;

const CAROUSEL_ITEMS_TO_SHOW_RESP = 5;
const CAROUSEL_ITEMS_TO_SHOW_MOBILE = 1.5;

type Props = {
    viewer: HpSharedNewArrivalsTabContent_viewer$key;
    user: HpSharedNewArrivalsTabContent_user$key | null;
    personalizationCollections: HpSharedNewArrivalsCarouselTabs_personalizationCollections$key | null;
};

export const HpSharedNewArrivalsCarouselTabs: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    personalizationCollections: personalizationCollectionsRef,
}) => {
    const { isMobile, userType } = useHomePageContext();
    const isClient = useClientState();
    const itemsToShow = isMobile ? CAROUSEL_ITEMS_TO_SHOW_MOBILE : CAROUSEL_ITEMS_TO_SHOW_RESP;
    const userId = isClient ? getBuyerId(document.cookie) || '' : '';
    const isTrade = userType === TRADE;

    const personalizationCollections = useFragment(
        personalizationCollectionsFragment,
        personalizationCollectionsRef
    );

    const collections = useMemo(
        () => personalizationCollections ?? [],
        [personalizationCollections]
    );

    const { tabList, tabMap } = useMemo(() => {
        const titles: string[] = [];
        const map: Record<string, HpCarouselTab> = {};

        for (const collection of collections) {
            const categoryTitle = collection?.categoryTitle;
            if (!categoryTitle || titles.includes(categoryTitle)) {
                continue;
            }
            titles.push(categoryTitle);
            const path = collection?.linkData?.path;
            map[categoryTitle] = {
                value: categoryTitle,
                label: categoryTitle,
                trigger: `New Arrivals - ${categoryTitle}`,
                viewMoreLink: path || '/new-arrivals/?sort=recommended',
            };
        }

        return { tabList: titles, tabMap: map };
    }, [collections]);

    const renderPanel = useCallback(
        (tabName: string): ReactNode => {
            const collection = collections.find(e => e?.categoryTitle === tabName);
            const itemIds = (collection?.categoryItems ?? [])
                .map(e => e?.serviceId)
                .filter(filterFalsy);

            if (!itemIds.length) {
                return null;
            }

            return (
                <Suspense
                    fallback={
                        <ItemTilesCarouselShimmer
                            fullWidth={!!isMobile}
                            gapSize="small"
                            itemsToShow={itemsToShow}
                            showArrows={!isMobile}
                            size="medium"
                        />
                    }
                >
                    <HpSharedNewArrivalsTabContent
                        itemIds={itemIds}
                        isTrade={isTrade}
                        isMobile={isMobile}
                        userId={userId}
                        userRef={userRef}
                        viewerRef={viewerRef}
                    />
                </Suspense>
            );
        },
        [collections, isMobile, isTrade, userId, itemsToShow, userRef, viewerRef]
    );

    return (
        <div
            className={classnames({
                [dibsCss.pyLarge]: isMobile,
            })}
            data-tn="new-arrivals-section"
        >
            {tabList.length ? (
                <HpSharedCarouselTabs
                    tabList={tabList}
                    tabMap={tabMap}
                    title={newArrivalsVariantTitle}
                    isMobile={isMobile}
                    renderPanel={renderPanel}
                    defaultActiveTab={tabList[0]}
                    renderOnInactive={false}
                />
            ) : (
                <HpSharedCarouselTabsShimmer isMobile={isMobile} itemsToShow={itemsToShow} />
            )}
        </div>
    );
};
